import { AxiosInstance } from "axios";
import { normalizeNotificationResourceType } from "settings/account/types/tag-resource-types";

/**
 * Resource picker data adapter.
 *
 * Each entry maps a `resourceType` (the same string used by the
 * notifications backend) to:
 *   - `endpoint`: the existing GET path the product's main view already
 *     uses to list resources of this type. Reused as-is, zero new
 *     backend endpoints.
 *   - `extract`: pulls the array of items out of the axios response.
 *   - `idField` / `labelField` / `subscriptionIdField` / `tagField` /
 *     `subtitleField` / `statusField`: which fields on each item to surface
 *     in the picker table. Undefined fields just render blank in their column.
 *
 * Adding a new resource type = add an entry here. No other change.
 */
export interface ResourceListItem {
  id: string;
  label: string;
  /** Human-readable subscription id (KE) or sub id (Reflector). */
  subscriptionId?: string | null;
  tag?: string | null;
  subtitle?: string | null;
  status?: string | null;
  /** Owning customer — populated when the list endpoint includes child services. */
  customer?: {
    name: string;
    chargebeeCustomerId?: string | null;
  } | null;
}

interface ListerConfig {
  endpoint: (tenantId: number) => string;
  /** axios .data unwrapper — defaults to `r => r.data?.data` */
  extract?: (response: { data: unknown }) => unknown[];
  idField?: string;
  labelField?: string;
  subscriptionIdField?: string;
  tagField?: string;
  subtitleField?: string;
  statusField?: string;
}

const SUBSCRIPTION_ID_FALLBACK_KEYS = [
  "subscriptionId",
  "subId",
  "subid",
  "serviceId",
] as const;

function readSubscriptionId(
  item: Record<string, unknown>,
  preferredField?: string
): string | null {
  const keys = preferredField
    ? [preferredField, ...SUBSCRIPTION_ID_FALLBACK_KEYS]
    : [...SUBSCRIPTION_ID_FALLBACK_KEYS];
  const seen = new Set<string>();
  for (const key of keys) {
    if (seen.has(key)) continue;
    seen.add(key);
    const raw = item[key];
    if (raw == null) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return null;
}

const DEFAULT_EXTRACT = (response: { data: unknown }): unknown[] => {
  const body = (response as { data?: { data?: unknown } }).data;
  const arr = body && typeof body === "object" && "data" in body ? (body as { data: unknown }).data : body;
  return Array.isArray(arr) ? arr : [];
};

const RESOURCE_LISTERS: Record<string, ListerConfig> = {
  port: {
    endpoint: (tid) =>
      `/kontroledge/ports?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    subscriptionIdField: "portId",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  "cloud-connectivity": {
    endpoint: (tid) =>
      `/kontroledge/cloud-connectivity?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  "datacenter-interconnect": {
    endpoint: (tid) =>
      `/kontroledge/datacenter-interconnect?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  // CTX-727 · the customer list (chopper-backend CTX-718); drafts never come.
  "carrier-bridge": {
    endpoint: (tid) =>
      `/kontroledge/carrier-bridge?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subscriptionId",
    statusField: "serviceStatus",
  },
  internet: {
    endpoint: (tid) =>
      `/kontroledge/internet?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  fwaas: {
    endpoint: (tid) =>
      `/kontroledge/fwaas?tenantId=${tid}&includeChildServices=true&includeFWInt=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  backbone: {
    endpoint: (tid) => `/kontroledge/bbaas?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "serviceId",
    subscriptionIdField: "subscriptionId",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  // "bb-connector": {
  //   endpoint: (tid) => `/kontroledge/bbaas/bb-connector?tenantId=${tid}&includeChildServices=true`,
  //   idField: "id",
  //   labelField: "name",
  //   tagField: "tag",
  //   subtitleField: "subid",
  //   statusField: "status",
  // },
  "sdwan-hub": {
    endpoint: (tid) => `/kontroledge/bbaas/sdwan-hub?tenantId=${tid}&includeChildServices=true`,
    idField: "id",
    labelField: "name",
    tagField: "tag",
    subtitleField: "subid",
    statusField: "status",
  },
  reflector: {
    // Reflector v2 lives outside the v1 baseURL — pass the absolute URL
    // and axios respects it (interceptors still add the Bearer token).
    endpoint: (tid) =>
      `${process.env.REACT_APP_API_URL ?? ""}/v2/reflector/instances?tenantId=${tid}`,
    idField: "id",
    labelField: "name",
    subscriptionIdField: "subId",
    tagField: "tag",
    subtitleField: "subId",
    statusField: "status",
  },
  cluster: {
    endpoint: (tid) =>
      `${process.env.REACT_APP_API_URL ?? ""}/v2/reflector/clusters?tenantId=${tid}`,
    idField: "id",
    labelField: "name",
    subscriptionIdField: "subId",
    tagField: "tag",
    subtitleField: "subId",
    statusField: "status",
  },
};

export function getSupportedResourceTypes(): string[] {
  return Object.keys(RESOURCE_LISTERS);
}

export async function listResourcesOfType(
  api: AxiosInstance,
  tenantId: number,
  resourceType: string
): Promise<ResourceListItem[]> {
  const config = RESOURCE_LISTERS[normalizeNotificationResourceType(resourceType)];
  if (!config) return [];
  const response = await api.get(config.endpoint(tenantId));
  const extract = config.extract ?? DEFAULT_EXTRACT;
  const items = extract(response) as Record<string, unknown>[];
  return items.map((item) => {
    const rawCustomer = item.customer as
      | { name?: string; chargebeeCustomerId?: string }
      | null
      | undefined;
    return {
      id: String(item[config.idField ?? "id"] ?? ""),
      label: String(
        item[config.labelField ?? "name"] ??
          item[config.idField ?? "id"] ??
          ""
      ),
      subscriptionId: readSubscriptionId(item, config.subscriptionIdField),
      tag: config.tagField
        ? (item[config.tagField] as string | null | undefined) ?? null
        : null,
      subtitle: config.subtitleField
        ? (item[config.subtitleField] as string | null | undefined) ?? null
        : null,
      status: config.statusField
        ? (item[config.statusField] as string | null | undefined) ?? null
        : null,
      customer: rawCustomer?.name
        ? {
            name: rawCustomer.name,
            chargebeeCustomerId: rawCustomer.chargebeeCustomerId ?? null,
          }
        : null,
    };
  });
}
